import { Table } from "antd";
import moment from "moment";
import { useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { addAdminActivity, getAttendanceFilterByModule, saveCertificateInfo, } from "../../../firebase/utils";
import AdminContext from "../../../Helper/admin";
import courseContext from "../../../Helper/Courses";
import StudentContext from "../../../Helper/Students";
import useFetchUserRoles from "../../../hook";
import { isAccess } from "../../../utils/adminRoles";
import { getCertificateColumn } from "../../../utils/certificateColumn";
import { getCertificateForCourse, handleDownloadCertificate, handleGenerateCertificate, handleSummaryFeedback, hasGeneratedCertificate, removeCertificateCompletely, updateStudentCourses } from "../../../utils/certificateHandles";
import { searchInArray } from "../../../utils/search";
import CertificateHeader from "./CertificateHeader";
import CertificateSummaryModal from "./CertificateSummaryModal";

const CertificateTable = () => {
  const { user, location } = useContext(AdminContext);
  const hasAccessToCertGen = useFetchUserRoles('certificate', isAccess);

  const { activeCourse } = useContext(courseContext);
  const { name: courseName, startDate, endDate, days, id: courseId, type } = activeCourse;

  const { students, loading: studentLoading, setStudents, fetchStudents } = useContext(StudentContext);

  const [search, setSearch] = useState("");
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [allAttendance, setAllAttendance] = useState([]);

  const [loading, setLoading] = useState({
    generateLoading: false,
    deleteLoading: false,
    email: null,
  });
  const [mainLoading, setMainLoading] = useState(false);

  const [generationSummary, setGenerationSummary] = useState({
    success: [],
    failed: [],
    skipped: [],
  });
  const [isSummaryModalVisible, setIsSummaryModalVisible] = useState(false);


  const handleSingleStudentCertificate = async (student, formattedDate, results) => {
    const { fullName, id: studentId, email } = student;

    try {
      if (hasGeneratedCertificate(student, courseId)) {
        results.skipped.push({ fullName, email });
        return;
      }
      let courseCategory = "Web & Mobile App Development";
      if (type === "beginner") {
        courseCategory = "Web Development"
      }
      const imageUrl = await handleGenerateCertificate(fullName, studentId, courseName, formattedDate, courseCategory);
      if (!imageUrl) {
        results.failed.push({ fullName, email, id: studentId, reason: 'Upload failed' });
        return;
      }

      const updatedCourses = await saveCertificateInfo(studentId, {
        courseId,
        url: imageUrl,
      });

      if (!updatedCourses) {
        results.failed.push({ fullName, email, id: studentId, reason: 'Certificate info update failed' });
        return;
      }

      await uploadAdminActivity(email, true);
      updateStudentData(studentId, updatedCourses);

      results.success.push({ fullName, email });
      toast.success(`Certificate generated successfully for ${fullName}.`);
    } catch (err) {
      console.error(`Error generating certificate for ${fullName}:`, err);
      results.failed.push({ fullName, email, id: studentId, reason: err.message || 'Unknown error' });
      toast.error(`Failed to generate certificate for ${fullName}.`);
    }
  };

  const handleGenerate = async (input) => {
    if (!hasAccessToCertGen) return toast.error("You don't have permission to generate certificates");

    const date = new Date();
    const formattedDate = moment(date).format('MMMM [of] YYYY');

    const studentsList = Array.isArray(input) ? input : [input];
    if (!studentsList.length) return toast.error("No students selected");

    toast.info(`Generating certificate${studentsList.length > 1 ? 's' : ''}...`);

    const results = {
      success: [],
      skipped: [],
      failed: [],
    };

    for (const student of studentsList) {
      await handleSingleStudentCertificate(student, formattedDate, results);
    }

    if (studentsList.length === 1) return;

    setGenerationSummary(results);
    setIsSummaryModalVisible(true);

    handleSummaryFeedback(studentsList.length, results);
  };

  const uploadAdminActivity = async (email, isGeneration) => {
    try {
      const activityType = isGeneration ? "Certificate_Generation" : "Certificate_Deletion";
      const activityDescription = isGeneration
        ? `${user.email} generated certificate for ${email}`
        : `${user.email} deleted certificate for ${email}`;

      const activity = {
        adminId: user?.email,
        activityOnUser: email,
        coordination: location,
        type: activityType,
        courseName: courseName,
        createdAt: new Date(),
        activity: activityDescription,
      };

      await addAdminActivity(activity, location);
    } catch (error) {
      console.error("Error adding admin activity:", error);
    }
  }

  const updateStudentData = (studentId, updatedCourses) => {
    if (!updatedCourses) return toast.error("Certificate generation failed");

    const args = { targetStudentId: studentId, updatedCourses };

    setStudents((prev) => updateStudentCourses(prev, args));
    setSelectedStudents((prev) => updateStudentCourses(prev, args));
  }

  useEffect(() => {
    const fetchAllAttendance = async () => {
      const courseAttendance = await getAttendanceFilterByModule(startDate, endDate);
      setAllAttendance(courseAttendance);
    }

    fetchAllAttendance();
  }, [students]);

  const processStudentsWithAttendance = (students, attendance) => {
    const classDays = days.toUpperCase().split(/[ &]+/);
    let newStartDate = new Date(startDate);
    let newEndDate = new Date(endDate);

    let totalClasses = 0;
    while (newStartDate <= newEndDate) {
      if (classDays.includes(newStartDate.toLocaleString("en-us", { weekday: "short" }).toUpperCase())) totalClasses++;

      newStartDate.setDate(newStartDate.getDate() + 1);
    }
    return students
      ?.filter(student => {
        const course = student.courses.find(course => course.courseId === courseId);
        return course?.feeScreenshots[1]?.status === "verified" && !student.banned;
      })
      .map(student => {
        const studentAttendance = attendance.filter(
          (entry) => entry.userId === student.email
        );

        const presentClasses = studentAttendance.filter(
          (entry) => entry.status === "present"
        ).length;

        const attendancePercent = Math.round(presentClasses/totalClasses * 100)

        return {
          ...student,
          presentClasses,
          totalClasses,
          attendanceRatio: `${presentClasses}/${totalClasses} (${attendancePercent}%)`,
        };
      }).sort((a, b) => b.presentClasses - a.presentClasses);
  };

  const cleanStudentData = processStudentsWithAttendance(students, allAttendance);

  const filteredStudent = useMemo(() => {
    const keys = ['studentId', 'email', 'fullName']
    const studentFilteredData = searchInArray(cleanStudentData, keys, search)
    return studentFilteredData
  }, [search, cleanStudentData]);

  useEffect(() => {
    fetchStudents(activeCourse)
  }, [activeCourse]);

  const setOperationLoading = (type, email, value) => {
    setLoading((prev) => ({
      ...prev,
      [`${type}Loading`]: value,
      email: value ? email : null,
    }));
  };

  const onGenerateCertificate = async (student) => {
    setOperationLoading("generate", student?.email, true);
    await handleGenerate(student);
    setOperationLoading("generate", null, false);
  };

  const onDownloadCertificate = (student) => {
    const certificate = getCertificateForCourse(student, courseId);
    handleDownloadCertificate([certificate], student.email);
  };

  const onDeleteCertificate = async (student) => {
    setOperationLoading("delete", student?.email, true);

    const updatedCourses = await removeCertificateCompletely({
      studentId: student?.id,
      courseId,
      courseName,
    });

    await uploadAdminActivity(student?.email, false);
    updateStudentData(student?.id, updatedCourses);

    setOperationLoading("delete", null, false);
  };

  const columnData = getCertificateColumn({
    onGenerate: onGenerateCertificate,
    onDownload: onDownloadCertificate,
    onDelete: onDeleteCertificate,
    loading,
    mainLoading,
    setLoading,
    activeCourse,
  });

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedStudents(selectedRows);
    },
    getCheckboxProps: (record) => ({
      disabled: record.banned,
    }),
  };

  const summaryModalProps = {
    generationSummary,
    isSummaryModalVisible,
    setIsSummaryModalVisible,
    setGenerationSummary,
    handleGenerate
  }

  return (
    <>
      <CertificateSummaryModal summaryModalProps={summaryModalProps} />

      <CertificateHeader
        activeCourse={activeCourse}
        search={search}
        setSearch={setSearch}
        loading={studentLoading}
        filteredStudent={filteredStudent}
        selectedStudents={selectedStudents}
        handleGenerate={handleGenerate}
        handleDownload={handleDownloadCertificate}
        mainLoading={mainLoading}
        setMainLoading={setMainLoading}
      />
      <div className='m-b-50'>
        <Table
          loading={studentLoading}
          rowKey="studentId"
          rowSelection={rowSelection}
          rowClassName={(record) => record.banned ? 'banned-student' : ''}
          columns={columnData}
          dataSource={filteredStudent}
          bordered
          pagination={false}
          scroll={{ x: "auto" }}
        />
      </div>
    </>
  )
}

export default CertificateTable;